import Joi from 'joi-browser';
import regex from '../../utilities/validateRegex';
import {
  PRESENCE,
  isConstructionIndustryPermissionStatus,
  isAcceptanceForeignWorkersStatus,
  isOtherIndustryPermissionStatus,
} from '../../utilities/companySafetyUtil';
import {
  customValidateDate,
  customValidateDateNotAllowEmpty,
} from '../validateDateUtil';
import {
  PREFECTURE_GROUPS,
  EXISTANCE_EXCLUSION_FLAG,
} from '../../utilities/constants';
import { INDUSTRY } from '../../utilities/roanConstants';
import { EXISTANCE_FLAG } from '../constants';

export const companySaftyEditSchema = () => {
  const prefecturesCodeIdSchema = () => {
    return {
      prefectures_code_id: Joi.when('permitted_source_section', {
        is: INDUSTRY.permissions.source[2].id,
        then: Joi.number()
          .label('must')
          .required(),
      }), // 許可都道府県ID
    };
  };

  return {
    // 代表者情報
    company_detail: Joi.object().keys({
      delegate_name: Joi.string().allow(null, ''), // 代表者名
      delegate_name_kana: Joi.string()
        .label('katakana')
        .regex(regex.pattern.katakana)
        .allow(null, ''), // 代表者名フリガナ
      delegate_post: Joi.string().allow(null, ''), // 代表者名役職
      construction_industry_permission_flag: Joi.number().allow(null, ''), // 建設業許可有無フラグ
      acceptance_foreign_workers_flag: Joi.number().allow(null, ''), // 外国人就労者受入有無
    }),
    // 許可情報
    company_branch_permissions: Joi.array().items(
      Joi.object().keys({
        permitted_source_section: Joi.number().allow(null, ''), // 許可元区分名
        ...prefecturesCodeIdSchema(),

        construction_industry_permission_section: Joi.number().allow(null, ''), // 建設許可区分名
        authorized_industry_codes: Joi.string().allow(null, ''), // 許可業種コード
        authorized_industry_names: Joi.string().allow(null, ''), // 許可業種名
        permission_number_l: Joi.string()
          .label('integerNoSign')
          .regex(regex.pattern.integerNoSign)
          .allow(null, ''), // 許可番号 年度
        permission_number_r: Joi.string()
          .label('integerNoSign')
          .regex(regex.pattern.integerNoSign)
          .allow(null, ''), // 許可番号 固有番号
        license_date: customValidateDate.base().validateDate(), // 許可年月日
        license_update_date: customValidateDate.base().validateDate(), // 許可更新年月日
      })
    ),
    // 保険情報
    company_branch_insurance: Joi.object().keys({
      health_insurance_flag: Joi.string().allow(null, ''), // 健康保険_加入状況
      ccus_health_insurance_reason_exclusion_code: Joi.string().allow(null, ''), // 社会保険適用外理由コード_健保
      health_insurance_reason_exclusion: Joi.string().allow(null, ''), // 健康保険_適用除外理由
      health_insurance_type_code: Joi.string().allow(null, ''), // 健康保険_健康保険種類コード
      health_insurance_type: Joi.string().allow(null, ''), // 健康保険_保険種類
      health_insurance_name_insurer: Joi.string().allow(null, ''), // 健康保険_保険者名称
      health_insurance_office_symbol: Joi.string().allow(null, ''), // 健康保険_事業所整理記号
      health_insurance_office_number: Joi.string()
        .label('integerNoSign')
        .regex(regex.pattern.integerNoSign)
        .allow(null, ''), // 健康保険_事業所番号

      pension_flag: Joi.string().allow(null, ''), // 年金保険_加入の有無
      ccus_pension_reason_exclusion_code: Joi.string().allow(null, ''), // 社会保険適用外理由コード_年金
      pension_reason_exclusion: Joi.string().allow(null, ''), // 年金保険_適用除外理由
      pension_type_code: Joi.string().allow(null, ''), // 年金保険_年金種類コード
      pension_type: Joi.string().allow(null, ''), // 年金保険_年金保険種類名称
      pension_office_symbol: Joi.string().allow(null, ''), // 年金保険_事業所整理記号
      pension_office_number: Joi.string()
        .label('integerNoSign')
        .regex(regex.pattern.integerNoSign)
        .allow(null, ''), // 年金保険_事業所番号
      employment_insurance_flag: Joi.string().allow(null, ''), // 雇用保険_加入状況
      ccus_employment_insurance_reason_exclusion_code: Joi.string().allow(
        null,
        ''
      ), // 社会保険適用外理由コード_雇用
      employment_insurance_reason_exclusion: Joi.string().allow(null, ''), // 雇用保険_適用除外理由
      employment_insurance_insurance_number: Joi.string()
        .label('hankakuIntegerSymbol')
        .regex(regex.pattern.hankakuIntegerSymbol)
        .allow(null, ''), // 雇用保険_労働保険番号
      worker_compensation_flag: Joi.string().allow(null, ''), // 労災保険特別加入_加入状況
      worker_compensation_number: Joi.string()
        .label('hankakuAlphaNumSymbol')
        .regex(regex.pattern.hankaku)
        .allow(null, ''), // 労災保険特別加入_労働保険番号
      worker_compensation_organize_number: Joi.string()
        .label('hankakuAlphaNumSymbol')
        .regex(regex.pattern.hankaku)
        .allow(null, ''), // 労災保険特別加入_整理番号
      worker_add_on_compensation_flag: Joi.string().allow(null, ''), // 労災保険_労災上乗保険_加入状況
      const_subscription_flag: Joi.string().allow(null, ''), // 建設業退職金共済制度加入状況
      const_subscription_number: Joi.string()
        .label('hankakuIntegerSymbol')
        .regex(regex.pattern.hankakuIntegerSymbol)
        .allow(null, ''), // 建設業退職金共済契約者番号
      enterprise_subscription_flag: Joi.string().allow(null, ''), // 中小企業退職金共済制度_加入状況
      enterprise_subscription_number: Joi.string()
        .label('hankakuIntegerSymbol')
        .regex(regex.pattern.hankakuIntegerSymbol)
        .allow(null, ''), // 中小企業退職金共済契約者番号
    }),
    // 外国人就労者情報
    company_branch_foreigner: Joi.object().keys({
      certification_number: Joi.string().allow(null, ''), // 適正監理計画認定番号
      supervisor_worker_id: Joi.string().allow(null, ''), // ワーカーID_受入監理責任者
      supervisor_name: Joi.string().allow(null, ''), // ワーカー名_受入監理責任者
      supervisor_position_name: Joi.string().allow(null, ''), // 役職名_受入監理責任者
      supervisor_tel: Joi.string()
        .label('tel')
        .regex(regex.pattern.tel)
        .allow('--', null, ''), // 受入監理責任者電話番号
      leader_worker_id: Joi.string().allow(null, ''), // ワーカーID_受入監理指導員
      work_place: Joi.string().allow(null, ''), // 外国人就労者_就労場所
      engagement_content: Joi.string().allow(null, ''), // 外国人就労者_従事業務内容
      work_category: Joi.string().allow(null, ''), // 外国人就労者_業務区分
      engagement_start_date: customValidateDate.base().validateDate(), // 外国人就労者_従事開始年月日
      engagement_end_date: customValidateDate.base().validateDate(), // 外国人就労者_従事終了年月日
    }),
  };
};

/**
 * 安全書類項目で未入力のものを確認する用
 * エラーメッセージの表示順序をフォームの表示順番と揃える必要があるため
 * バリデーションの順番はフォームの順番と揃える
 */
export const schemaForSafetyDoc = ({
  healthInsuranceStatus,
  pensionInsuranceStatus,
  employmentInsuranceStatus,
  workerCompensationStatus,
  constSubscriptionStatus,
  otherSubscriptionStatus,
  acceptanceForeignWorkersFlag,
  otherIndustryPermissionFlag,
  isPartnerCompanySafty = false, // 協力会社の会社情報のみ建退共制度加入状況、中退共制度加入状況が安全書類項目のため
  isPartnerCompanyBranchSafty = false, // 協力会社の支店の労務安全項目は「外国人就労者情報」の入力項目がないため
}) => {
  const companyDetail = isPartnerCompanyBranchSafty
    ? {
        company_detail: Joi.object().keys({}),
      }
    : {
        company_detail: Joi.object().keys({
          delegate_name: Joi.string().required(), // 代表者名
          delegate_post: Joi.string().required(), // 代表者役職
          acceptance_foreign_workers_flag: Joi.number().required(), // 外国人就労者受入有無
        }),
      };

  const companyBranchInsuranceSafetyDocItem = Joi.when(
    'health_insurance_flag',
    {
      is: EXISTANCE_EXCLUSION_FLAG.EXIST.ID,
      then: Joi.string().required(),
    }
  ).when('health_insurance_flag', {
    is: EXISTANCE_EXCLUSION_FLAG.EXCLUSION.ID,
    then: Joi.when('health_insurance_reason_exclusion', {
      is: Joi.string().valid('', 'その他'),
      then: Joi.string().required(),
    })
      .when('health_insurance_reason_exclusion', {
        is: '５人未満個人事業所',
        then: Joi.when('health_insurance_type', {
          is: Joi.string().valid('国民健康保険組合', ''),
          then: Joi.string().required(),
        }),
      })
      .when('health_insurance_reason_exclusion', {
        is: 'けんぽ適用除外承認済',
        then: Joi.string().required(),
      }),
  });

  const companyBranchInsurance = (
    healthInsuranceStatus,
    otherSubscriptionStatus,
    pensionInsuranceStatus,
    employmentInsuranceStatus
  ) => {
    const health = healthInsuranceStatus => {
      const required = {
        health_insurance_flag: Joi.string().required(), // 健康保険_加入状況,
        health_insurance_name_insurer: companyBranchInsuranceSafetyDocItem,
        health_insurance_office_symbol: companyBranchInsuranceSafetyDocItem,
        health_insurance_office_number: companyBranchInsuranceSafetyDocItem,
      };

      return required;
    };
    const other = otherSubscriptionStatus => {
      let constSubscriptionFlagAndEnterpriseSubscriptionFlagSchema = {};
      if (isPartnerCompanySafty) {
        constSubscriptionFlagAndEnterpriseSubscriptionFlagSchema = {
          const_subscription_flag: Joi.string().required(),
          enterprise_subscription_flag: Joi.string().required(), // 中退共制度加入状況
          other_subscription_flag: Joi.string().required(), // その他退職金共済制度加入状況
        };
      }
      if (!otherSubscriptionStatus) {
        return {
          ...constSubscriptionFlagAndEnterpriseSubscriptionFlagSchema,
        };
      }
    };
    const pension = pensionInsuranceStatus => {
      const required = {
        pension_flag: Joi.string().required(), // 年金保険_加入状況,
        pension_office_symbol: Joi.when('pension_flag', {
          is: EXISTANCE_EXCLUSION_FLAG.EXIST.ID,
          then: Joi.string().required(), // 年金保険_事業所整理記号,
        }),
        pension_office_number: Joi.when('pension_flag', {
          is: EXISTANCE_EXCLUSION_FLAG.EXIST.ID,
          then: Joi.string().required(), // 年金保険_事業所番号,
        }),
      };
      if (!pensionInsuranceStatus) {
        return {
          ...required,
        };
      }
      if (pensionInsuranceStatus.key === PRESENCE) {
        return {
          ...required,
          pension_office_symbol: Joi.string().required(), // 年金保険_事業所整理記号
          pension_office_number: Joi.string().required(), // 年金保険_事業所番号
        };
      }
      return {
        ...required,
      };
    };
    const employment = employmentInsuranceStatus => {
      const required = {
        employment_insurance_flag: Joi.string().required(), // 雇用保険_加入状況
      };
      if (!employmentInsuranceStatus) {
        return {
          ...required,
        };
      }
      if (employmentInsuranceStatus.key === PRESENCE) {
        return {
          ...required,
          employment_insurance_insurance_number: Joi.string().required(), // 雇用保険_労働保険番号
        };
      }
      return {
        ...required,
      };
    };
    return {
      company_branch_insurance: Joi.object().keys({
        ...health(healthInsuranceStatus),
        ...other(otherSubscriptionStatus),
        ...pension(pensionInsuranceStatus),
        ...employment(employmentInsuranceStatus),
      }),
    };
  };

  const companyBranchForeigner = acceptanceForeignWorkersFlag => {
    if (!isAcceptanceForeignWorkersStatus(acceptanceForeignWorkersFlag)) {
      return {};
    }
    return {
      company_branch_foreigner: Joi.object().keys({
        supervisor_worker_id: Joi.string()
          .allow(null)
          .required(), // ワーカーID_受入監理責任者
        supervisor_name: Joi.string().required(), // ワーカー名_受入監理責任者
        supervisor_position_name: Joi.string().required(), // 役職名_受入監理責任者
        supervisor_tel: Joi.string().required(), // 受入監理責任者電話番号
        // leader_worker_id: Joi.string().required(), // ワーカーID_受入監理指導員
        work_category: Joi.string().required(), // 外国人就労者_業務区分
        engagement_start_date: Joi.string().required(), // 外国人就労者_従事開始年月日
        engagement_end_date: Joi.string().required(), // 外国人就労者_従事終了年月日
      }),
    };
  };

  const otherCompanyBranchPermissionGroups = otherIndustryPermissionFlag => {
    if (!isOtherIndustryPermissionStatus(otherIndustryPermissionFlag)) {
      return {};
    }
    return {
      other_company_branch_permission_groups: Joi.array().items(
        Joi.object({
          license_limit_date: Joi.string().required(),
        })
      ),
    };
  };

  return Object.assign(
    {},
    companyDetail,
    companyBranchInsurance(
      healthInsuranceStatus,
      otherSubscriptionStatus,
      pensionInsuranceStatus,
      employmentInsuranceStatus
    ),
    companyBranchForeigner(acceptanceForeignWorkersFlag),
    otherCompanyBranchPermissionGroups(otherIndustryPermissionFlag)
  );
};

export const schemaForWorkerAddOnCompensation = workerAddOnCompensationStatus => {
  if (workerAddOnCompensationStatus) {
    return {
      worker_add_on_compensation_start_date: customValidateDate
        .base()
        .validateDate(),
      worker_add_on_compensation_end_date: customValidateDate
        .base()
        .validateDate(),
      worker_add_on_compensation_amount: Joi.string()
        .label('integerNoSign')
        .regex(regex.pattern.integerNoSign)
        .max(12)
        .error(customErrorMessage)
        .allow(null, ''),
      worker_add_on_compensation_company_name: Joi.string()
        .max(100)
        .error(customErrorMessage)
        .allow(null, ''),
      worker_add_on_compensation_number: Joi.string()
        .label('hankakuAlphabetAndNumber')
        .regex(regex.pattern.hankakuAlphabetAndNumber)
        .max(20)
        .error(customErrorMessage)
        .allow(null, ''),
    };
  }
  return {};
};

export const companySecuritySaftyEditSchema = () => {
  return {
    security_company_branch_permission_group: Joi.object().keys({
      prefectures_code_id: Joi.number().required(),
      permission_number_r: Joi.string()
        .label('integerNoSign')
        .regex(regex.pattern.integerNoSign)
        .max(20)
        .required()
        .error(customErrorMessage),
      license_update_date: customValidateDateNotAllowEmpty
        .base()
        .validateDate()
        .requiredDate(),
    }),
  };
};

const customErrorMessage = errors => {
  const [error] = errors;
  if (error.type === 'string.regex.base') {
    if (error.context && error.context.label) {
      error.context.name = error.context.label;
    }
  }
  return errors;
};

export const schemaOtherIndustryLicense = Joi.object().keys({
  company_detail: {
    other_industry_permission_flag: Joi.any(),
  },
  other_company_branch_permission_groups: Joi.array().when(
    'company_detail.other_industry_permission_flag',
    {
      is: Joi.valid(
        EXISTANCE_FLAG.EXIST.VALUE,
        String(EXISTANCE_FLAG.EXIST.VALUE)
      ),
      then: Joi.array()
        .min(1)
        .items(
          Joi.object().keys({
            invalid_flag: Joi.boolean().valid(false),
          })
        ),
    }
  ),
});

export default companySaftyEditSchema;



// WEBPACK FOOTER //
// ./src/utilities/validationSchemas/companySaftyEdit.js